import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotification } from '../contexts/NotificationContext';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import DashboardAccessGroupsAdmin from './DashboardAccessGroupsAdmin';
import {
  DASHBOARD_ACCESS_LEVELS,
  normalizeManagerGrants,
  managerHasGrant,
  getManagerGrantLevel,
} from '../utils/dashboardAccess';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function principalLabel(m) {
  const full = `${m.name || ''} ${m.surname || ''}`.trim();
  if (full && m.email) return `${full} (${m.email})`;
  return full || m.email || m.username || m.id;
}

const RECIPIENT_TABS = [
  {
    principalType: 'Admin',
    label: 'Admins',
    hint: 'Administradores de la organización (VAR).',
    searchPlaceholder: 'Buscar admin...',
  },
  {
    principalType: 'Manager',
    label: 'Managers',
    hint: 'Managers del cliente de esta instancia.',
    searchPlaceholder: 'Buscar manager...',
  },
];

function matchesSearch(text, q) {
  if (!q) return true;
  return String(text || '').toLowerCase().includes(q);
}

const DashboardAccessAdmin = ({ open, getAuthHeaders, embedded = false }) => {
  const { showSuccess, showError } = useNotification();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [managersError, setManagersError] = useState(null);
  const [adminsError, setAdminsError] = useState(null);
  const [customerIdConfigured, setCustomerIdConfigured] = useState(true);
  const [managers, setManagers] = useState([]);
  const [directRecipients, setDirectRecipients] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [accessByManager, setAccessByManager] = useState({});
  const [accessByGroup, setAccessByGroup] = useState({});
  const [managersByGroup, setManagersByGroup] = useState({});
  const [selectedManagerIds, setSelectedManagerIds] = useState(() => new Set());
  const [dirtyManagerIds, setDirtyManagerIds] = useState(() => new Set());
  const [recipientTab, setRecipientTab] = useState(0);
  const [managerSearch, setManagerSearch] = useState('');
  const [dashboardSearch, setDashboardSearch] = useState('');

  const loadDataMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/admin/dashboard-access', { headers }),
  });

  const loadData = useCallback(() => {
    const headers = getAuthHeaders?.();
    if (!headers) return Promise.resolve();
    setLoading(true);
    setError(null);
    return loadDataMutation
      .mutateAsync(headers)
      .then((res) => {
        const data = res.data || {};
        setCustomerIdConfigured(data.customerIdConfigured !== false);
        setManagers(Array.isArray(data.managers) ? data.managers : []);
        setAdminsError(data.adminsError || null);
        setDirectRecipients(
          Array.isArray(data.directRecipients) ? data.directRecipients : data.managers || []
        );
        setManagersError(data.managersError || null);
        setDashboards(Array.isArray(data.dashboards) ? data.dashboards : []);
        setGroups(Array.isArray(data.groups) ? data.groups : []);
        setAccessByGroup(data.accessByGroup || {});
        setManagersByGroup(data.managersByGroup || {});
        const matrix = data.accessByManager || {};
        const normalized = {};
        for (const [mid, rows] of Object.entries(matrix)) {
          normalized[mid] = normalizeManagerGrants(rows);
        }
        setAccessByManager(normalized);
        setDirtyManagerIds(new Set());
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message || 'Error al cargar accesos');
      })
      .finally(() => setLoading(false));
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const managerQuery = managerSearch.trim().toLowerCase();
  const dashboardQuery = dashboardSearch.trim().toLowerCase();

  const recipientsByType = useMemo(() => {
    const byType = { Admin: [], Manager: [] };
    for (const principal of directRecipients) {
      const bucket = principal.principalType === 'Admin' ? 'Admin' : 'Manager';
      byType[bucket].push(principal);
    }
    return byType;
  }, [directRecipients]);

  const activeRecipientTab = RECIPIENT_TABS[recipientTab] || RECIPIENT_TABS[0];

  const filteredRecipients = useMemo(() => {
    const rows = recipientsByType[activeRecipientTab.principalType] || [];
    return rows.filter((m) => {
      const haystack = [m.name, m.surname, m.email, m.username, m.id].join(' ').toLowerCase();
      return matchesSearch(haystack, managerQuery);
    });
  }, [recipientsByType, activeRecipientTab, managerQuery]);

  const selectionSummary = useMemo(() => {
    const counts = { Admin: 0, Manager: 0 };
    for (const principal of directRecipients) {
      if (!selectedManagerIds.has(principal.id)) continue;
      counts[principal.principalType === 'Admin' ? 'Admin' : 'Manager'] += 1;
    }
    return counts;
  }, [directRecipients, selectedManagerIds]);

  const filteredDashboards = useMemo(() => {
    return dashboards.filter((d) => {
      const haystack = [d.name, d.publicId, d.database, d.id, d.ownerUserId, d.ownerManagerId]
        .join(' ')
        .toLowerCase();
      return matchesSearch(haystack, dashboardQuery);
    });
  }, [dashboards, dashboardQuery]);

  const selectedManagers = useMemo(() => [...selectedManagerIds], [selectedManagerIds]);

  const toggleManagerSelection = (managerId) => {
    setSelectedManagerIds((prev) => {
      const next = new Set(prev);
      if (next.has(managerId)) next.delete(managerId);
      else next.add(managerId);
      return next;
    });
  };

  const selectAllFilteredManagers = () => {
    setSelectedManagerIds((prev) => {
      const next = new Set(prev);
      filteredRecipients.forEach((m) => next.add(m.id));
      return next;
    });
  };

  const clearManagerSelection = () => setSelectedManagerIds(new Set());

  const markManagersDirty = (ids) => {
    setDirtyManagerIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleDashboardAccess = (dashboardId, checked) => {
    if (selectedManagers.length === 0) return;
    setAccessByManager((prev) => {
      const next = { ...prev };
      for (const mid of selectedManagers) {
        const current = normalizeManagerGrants(next[mid]);
        const id = Number(dashboardId);
        if (checked) {
          const without = current.filter((g) => g.dashboardId !== id);
          next[mid] = [...without, { dashboardId: id, accessLevel: 'view' }];
        } else {
          next[mid] = current.filter((g) => g.dashboardId !== id);
        }
      }
      return next;
    });
    markManagersDirty(selectedManagers);
  };

  const setDashboardAccessLevel = (dashboardId, accessLevel) => {
    if (selectedManagers.length === 0) return;
    const level = accessLevel === 'edit' ? 'edit' : 'view';
    setAccessByManager((prev) => {
      const next = { ...prev };
      for (const mid of selectedManagers) {
        const current = normalizeManagerGrants(next[mid]);
        const id = Number(dashboardId);
        if (!managerHasGrant(current, id)) continue;
        next[mid] = [
          ...current.filter((g) => g.dashboardId !== id),
          { dashboardId: id, accessLevel: level },
        ];
      }
      return next;
    });
    markManagersDirty(selectedManagers);
  };

  const dashboardCheckState = (dashboardId) => {
    if (selectedManagers.length === 0) {
      return { checked: false, indeterminate: false, disabled: true, accessLevel: 'view' };
    }
    const states = selectedManagers.map((mid) => managerHasGrant(accessByManager[mid], dashboardId));
    const levels = selectedManagers
      .filter((mid) => managerHasGrant(accessByManager[mid], dashboardId))
      .map((mid) => getManagerGrantLevel(accessByManager[mid], dashboardId));
    const all = states.every(Boolean);
    const some = states.some(Boolean);
    const allSameLevel = levels.length > 0 && levels.every((l) => l === levels[0]);
    return {
      checked: all,
      indeterminate: !all && some,
      disabled: false,
      accessLevel: allSameLevel ? levels[0] : 'view',
      levelIndeterminate: some && !allSameLevel,
    };
  };

  const saveAccessMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.put('/api/admin/dashboard-access', body, { headers }),
  });

  const handleSaveAccess = async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    const idsToSave = dirtyManagerIds.size > 0 ? [...dirtyManagerIds] : selectedManagers;
    if (idsToSave.length === 0) {
      showError('Selecciona al menos un usuario y modifica sus accesos');
      return;
    }
    setSaving(true);
    try {
      const updates = idsToSave.map((managerId) => ({
        managerId,
        grants: normalizeManagerGrants(accessByManager[managerId]),
      }));
      const res = await saveAccessMutation.mutateAsync({ body: { updates }, headers });
      const matrix = res.data?.accessByManager || accessByManager;
      const normalized = {};
      for (const [mid, rows] of Object.entries(matrix)) {
        normalized[mid] = normalizeManagerGrants(rows);
      }
      setAccessByManager(normalized);
      setDirtyManagerIds(new Set());
      showSuccess(`Accesos directos guardados para ${updates.length} usuario(s)`);
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'No se pudieron guardar los accesos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ py: embedded ? 0 : 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Acceso directo" />
          <Tab label="Grupos" />
        </Tabs>
        <Button variant="text" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
          Recargar
        </Button>
      </Box>

      {!customerIdConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se pueden listar managers: falta configuración interna del servidor.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {managersError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {managersError}
        </Alert>
      )}
      {adminsError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No se pudo cargar el directorio completo de admins. Se muestran el admin actual y los
          admins detectados como dueños de dashboards.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tab} index={0}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Asignación directa de admins y managers a dashboards. Los grupos siguen siendo solo para managers.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                minHeight: 360,
              }}
            >
              <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 360 }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={recipientTab}
                    onChange={(_, v) => setRecipientTab(v)}
                    variant="fullWidth"
                    sx={{ mb: 1, minHeight: 36 }}
                  >
                    {RECIPIENT_TABS.map((t) => (
                      <Tab
                        key={t.principalType}
                        sx={{ minHeight: 36 }}
                        label={`${t.label} (${(recipientsByType[t.principalType] || []).length})`}
                      />
                    ))}
                  </Tabs>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {activeRecipientTab.hint}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={activeRecipientTab.searchPlaceholder}
                    value={managerSearch}
                    onChange={(e) => setManagerSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button size="small" onClick={selectAllFilteredManagers}>
                      Seleccionar visibles
                    </Button>
                    <Button size="small" onClick={clearManagerSelection}>
                      Limpiar
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {`${selectionSummary.Admin} admin · ${selectionSummary.Manager} manager seleccionados`}
                    {dirtyManagerIds.size > 0 ? ` · ${dirtyManagerIds.size} con cambios sin guardar` : ''}
                  </Typography>
                </Box>
                <List dense sx={{ overflow: 'auto', flex: 1 }}>
                  {filteredRecipients.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        primary={`Sin ${activeRecipientTab.label.toLowerCase()} para mostrar`}
                      />
                    </ListItem>
                  )}
                  {filteredRecipients.map((m) => (
                    <ListItem key={m.id} disablePadding>
                      <ListItemButton onClick={() => toggleManagerSelection(m.id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedManagerIds.has(m.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={principalLabel(m)}
                          secondary={m.username ? `@${m.username}` : m.id}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 360 }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Dashboards ({filteredDashboards.length})
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar dashboard..."
                    value={dashboardSearch}
                    onChange={(e) => setDashboardSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <List dense sx={{ overflow: 'auto', flex: 1 }}>
                  {filteredDashboards.map((d) => {
                    const state = dashboardCheckState(d.id);
                    return (
                      <ListItem key={d.id} disablePadding sx={{ alignItems: 'flex-start' }}>
                        <ListItemButton
                          onClick={() => toggleDashboardAccess(d.id, !state.checked)}
                          disabled={state.disabled}
                          dense
                          sx={{ flex: 1 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={state.checked}
                              indeterminate={state.indeterminate}
                              disabled={state.disabled}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={d.name || `Dashboard ${d.id}`}
                            secondary={[d.publicId ? `ID ${d.publicId}` : null, d.database ? `BD ${d.database}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          />
                        </ListItemButton>
                        {state.checked && (
                          <FormControl
                            size="small"
                            sx={{ minWidth: 120, mr: 1, mt: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Select
                              value={state.levelIndeterminate ? 'view' : state.accessLevel}
                              displayEmpty={state.levelIndeterminate}
                              onChange={(e) => setDashboardAccessLevel(d.id, e.target.value)}
                              renderValue={
                                state.levelIndeterminate
                                  ? () => 'Mixto'
                                  : undefined
                              }
                            >
                              {Object.entries(DASHBOARD_ACCESS_LEVELS).map(([key, meta]) => (
                                <MenuItem key={key} value={key}>
                                  {meta.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveAccess}
                disabled={saving || (dirtyManagerIds.size === 0 && selectedManagers.length === 0)}
              >
                Guardar accesos directos
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <DashboardAccessGroupsAdmin
              getAuthHeaders={getAuthHeaders}
              managers={managers}
              dashboards={dashboards}
              groups={groups}
              accessByGroup={accessByGroup}
              managersByGroup={managersByGroup}
              onReload={loadData}
            />
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default DashboardAccessAdmin;
